/** @jsxImportSource @emotion/react */
import {
  Alert,
  Drawer,
  IconButton,
  Container,
  Box,
  Stack,
  Fab,
} from "@mui/material";
import PlaybookToc from "./ViewerPlaybookToc.js";
import ViewerPlaybookToolbar from "./ViewerPlaybookToolbar.js";
import { useState, useRef } from "react";
import Helper from "../../playcreator/functions/Helper.js";
import produce from "immer";
import PlayCreatorAd from "./PlayCreatorAd.js";
import ViewerPlaybookContent from "./ViewerPlaybookContent.js";
import { useIntersectionObserver } from "usehooks-ts";
import Iconify from "../../components/Iconify";
import Scrollbar from "../../components/Scrollbar";
import useIsFirstRender from "../../hooks/useIsFirstRender.js";
// -----------------------------------------x-----------------------------

export default function Playbook(props) {
  const [scrollToObject, setScrollToObject] = useState({
    chapter: null,
    object: null,
  });

  const [showDrawer, setShowDrawer] = useState(false);

  let playbookData = {
    ...props.playbook,
    ...Helper.convertToLatestPlaybookDataModel(props.playbook),
  };

  let plays = (playbookData.plays ?? []).map((data) => {
    if (data.error) {
      return {
        ...data,
      };
    }
    return {
      ...data,
      data: Helper.convertToLatestDataModel(data),
      title: data.title ?? data.data.settings.title,
    };
  });

  let playbook = produce(playbookData, (draftPlaybook) => {
    for (let play of plays) {
      draftPlaybook.data.chapters.forEach((chapter) => {
        chapter.objects.forEach((object) => {
          if (object.type === "play" && object.play_id == play.play_id) {
            object.data = play;
          }
        });
      });
    }
  });

  let isError = props.playbook.error ?? null;

  const [activeItem, setActiveItem] = useState({ chapter: 0, object: 0 });

  const tocRef = useRef();
  const entry = useIntersectionObserver(tocRef, {});

  const isTopTocVisible = entry?.isIntersecting;

  const playbookContent = Helper.getPlaybookContent(playbook.data);

  return (
    <Container
      sx={{ fontSize: 16, px: "0px !important", boxSizing: "border-box" }}
    >
      <Drawer
        anchor={"right"}
        open={showDrawer}
        onClose={() => {
          setShowDrawer(false);
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 400,
            maxWidth: "100vw",
            py: 5,
            px: 3,
          }}
        >
          <IconButton
            size="large"
            sx={{
              position: "absolute",
              top: 5,
              right: 5,
            }}
            onClick={() => setShowDrawer(false)}
          >
            <Iconify icon="mdi:close" />
          </IconButton>
          <PlaybookToc
            hideDrawer={() => {
              setShowDrawer(false);
            }}
            scrollToObject={(chapter, object) => {
              setScrollToObject({ chapter, object });
            }}
            chapters={playbook?.data?.chapters}
            sidebar={true}
            activeItem={activeItem}
          />
        </Box>
      </Drawer>

      {!isTopTocVisible && (
        <Fab
          onClick={() => {
            setShowDrawer(true);
          }}
          size="small"
          sx={{
            display: "flex",
            position: "fixed",
            bottom: 20,
            right: 20,
            "@media (min-width: 1300px)": {
              display: "none",
            },
          }}
          color="info"
          aria-label="add"
        >
          <Iconify sx={{ width: 25, height: 25 }} icon="material-symbols:toc" />
        </Fab>
      )}

      {!isError && <ViewerPlaybookToolbar playbookData={playbook} />}
      {isError && (
        <Alert sx={{ mt: 4 }} severity="error">
          {props.playbook.error}
        </Alert>
      )}

      <Stack direction="row">
        <Box
          sx={{
            flex: 1,
            maxWidth: 1000,
            minHeight: "80vh",
          }}
        >
          <Stack direction={{ xs: "column", lg: "row" }}>
            <Box sx={{ mt: 5, maxWidth: 800, width: "100%" }}>
              <Box
                component="h1"
                sx={{
                  fontSize: {
                    xs: 30,
                    sm: 38,
                    md: 45,
                  },
                }}
              >
                {playbook?.title}
              </Box>
              {playbook?.data && (
                <Box
                  ref={tocRef}
                  sx={{
                    display: "block",
                    "@media (min-width: 1300px)": {
                      display: "none",
                    },
                    mb: 10,

                    borderRadius: 2,
                    border: 1,
                    padding: 3,
                    borderColor: "grey.400",
                    mt: 4,
                  }}
                >
                  <PlaybookToc
                    hideDrawer={() => {
                      setShowDrawer(false);
                    }}
                    scrollToObject={(chapter, object) => {
                      setScrollToObject({ chapter, object });
                    }}
                    chapters={playbook?.data?.chapters}
                    sidebar={false}
                    activeItem={activeItem}
                  />
                </Box>
              )}

              <ViewerPlaybookContent
                setScrollToObject={setScrollToObject}
                scrollToObject={scrollToObject}
                setActiveItem={setActiveItem}
                content={playbookContent}
              />
            </Box>
          </Stack>
        </Box>

        <Box>
          <Box
            sx={(theme) => ({
              display: "none",
              position: "fixed",
              overflowY: "auto",
              width: 300,
              top: 100,
              right: 10,
              maxHeight: "calc( 100vh - 60px)",
              py: 2,
              pl: 2,
              bottom: 20,
              "@media (min-width: 1300px)": {
                display: "block",
              },
            })}
          >
            <Scrollbar>
              <Box sx={{ pr: 2 }}>
                {playbook?.data && (
                  <PlaybookToc
                    hideDrawer={() => {
                      setShowDrawer(false);
                    }}
                    scrollToObject={(chapter, object) => {
                      setScrollToObject({ chapter, object });
                    }}
                    chapters={playbook?.data?.chapters}
                    sidebar={true}
                    activeItem={activeItem}
                  />
                )}
              </Box>
            </Scrollbar>
          </Box>
        </Box>
      </Stack>
      <Box
        sx={{
          backgroundColor: "#fafafa",
          mt: 15,
          width: "100vw",
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
          textAlign: "center",
          pb: 2,
          borderTop: "1px solid #eeeeee",
        }}
      >
        <Box
          sx={{
            margin: "0 auto",
            maxWidth: 1200,
            padding: 0,
          }}
        >
          <PlayCreatorAd />
        </Box>
      </Box>
    </Container>
  );
}
